import { Card } from '@chakra-ui/react'
import { mdiAccount, mdiAccountMultiple, mdiMonitorOff, mdiOpenInNew } from '@mdi/js'
import Icon from '@mdi/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Flex } from '@/components/ui/flex'
import { Link } from '@/components/ui/link'
import { HStack, Stack } from '@/components/ui/stack'
import { Heading, Text } from '@/components/ui/typography'
import { useColorMode } from '@/hooks/useColorMode'
import useColorModeValue from '@/hooks/useColorModeValue'
import useCustomMediaQuery from '@/hooks/useCustomMediaQuery'
import { getDaysText, trackWidgetsClick } from '@/store/tasksApprovals/utils'
import { IWidget } from '@/types/tasksApprovals'

import SystemIcon from '../../mainContent/systemIcon'
import CompliancesContent from './components/compliancesContent'
import MyLearningContent from './components/compliancesContent/myLearning'
import InfoIcon from './InfoIcon'

const Widget = ({
    id,
    title,
    requestNumber,
    color,
    days,
    howMany,
    onClick,
    link,
    bodyText,
    isSelected,
    isCompliance,
    isExpanded = false,
    myCompliances,
    directsCompliances,
    isUnderMaintenance = false,
    status,
}: IWidget) => {
    const { colorMode } = useColorMode()
    const [textColor, setTextColor] = useState<string>('dark.700')
    const [isMobile] = useCustomMediaQuery()
    const { t } = useTranslation()

    useEffect(() => {
        const color: string = isSelected || colorMode !== 'light' ? 'var(--chakra-colors-white)' : 'var(--chakra-colors-dark-700)'
        setTextColor(color)
    }, [isSelected, colorMode])

    const onWidgetClick = () => {
        onClick(id)
        if (!isSelected) {
            void trackWidgetsClick(id, true)
        }
    }

    const unselectBackgroundProps = {
        backgroundSize: ['4px 100%', '4px 100%', '4px 100%', '8px 100%'],
        backgroundPosition: 'right',
        backgroundRepeat: 'no-repeat',
    }

    const cardClickProp = () => {
        if (!requestNumber) {
            return {}
        }
        return { onClick: onWidgetClick, cursor: 'pointer' }
    }

    const getCompliancesIcon = (numberOfTasks?: number) => {
        return numberOfTasks ? (
            <Icon
                path={mdiAccount}
                size="11px"
                color={isSelected || colorMode !== 'light' ? 'var(--chakra-colors-white)' : 'var(--chakra-colors-dark-700)'}
            />
        ) : (
            <Icon
                path={mdiAccountMultiple}
                size="11px"
                color={isSelected || colorMode !== 'light' ? 'var(--chakra-colors-white)' : 'var(--chakra-colors-dark-700)'}
            />
        )
    }

    const borderColor = useColorModeValue('var(--chakra-colors-surface-default)', 'var(--chakra-colors-dark-800)')
    const stackBackgroundColor = useColorModeValue('var(--chakra-colors-white)', 'var(--chakra-colors-dark-900)')
    const hasDirects = !!directsCompliances && (directsCompliances as number) > 0

    return (
        <>
            <Card.Root
                direction="row"
                overflow="hidden"
                variant="outline"
                minH={requestNumber > 0 && !isMobile ? '120px' : ''}
                borderRadius="8px"
                margin={isMobile ? '4px 6px 4px 6px' : '4px 6px 4px 4px'}
                border={!requestNumber ? '1px solid' : 'none'}
                borderColor={!requestNumber ? borderColor : ''}
                background={stackBackgroundColor}
                opacity={requestNumber ? '1' : '.5'}
                boxShadow={requestNumber && !isSelected ? '0px 0px 8px 1px rgba(84, 79, 64, 0.06)' : 'none'}
            >
                <Card.Body padding="0">
                    {((isMobile && isExpanded) || !isMobile) && (
                        <Stack
                            minH={requestNumber > 0 ? '120px' : ''}
                            p="16px"
                            width="100%"
                            background={color}
                            {...cardClickProp()}
                            {...(!isSelected && unselectBackgroundProps)}
                        >
                            <HStack
                                justifyContent="space-between"
                                marginBottom={requestNumber ? (id === 'MY_LEARNING' ? '8px' : '16px') : '0'}
                            >
                                <Heading
                                    fontSize="14px"
                                    fontWeight="bold"
                                    letterSpacing="0.25px"
                                    color={textColor}
                                    style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                                >
                                    {title} {!isUnderMaintenance && `(${requestNumber})`}
                                </Heading>
                                {link && (
                                    <Link data-id="chat-citation-link" href={link} isExternal mr="8px">
                                        <Icon
                                            path={mdiOpenInNew}
                                            size="16px"
                                            color={isSelected ? 'rgba(255, 255, 255, 0.88)' : 'var(--chakra-colors-secondary-600)'}
                                        />
                                    </Link>
                                )}
                            </HStack>
                            {isUnderMaintenance && (
                                <Flex mt="16px">
                                    <Icon path={mdiMonitorOff} size={1} />
                                    <Text pl="8px" textTransform="uppercase" color={textColor} fontSize="12px" maxW="80%">
                                        {t('tasksApprovals.widgets.underMaintenance')}
                                    </Text>
                                </Flex>
                            )}
                            {!!requestNumber &&
                                id === 'MY_LEARNING' &&
                                typeof directsCompliances !== 'undefined' &&
                                typeof myCompliances !== 'undefined' && (
                                    <MyLearningContent
                                        isSelected={isSelected}
                                        iconColor={textColor}
                                        textColor={textColor}
                                        directsCompliances={directsCompliances}
                                        myCompliances={myCompliances}
                                    />
                                )}
                            {!!requestNumber && id !== 'MY_LEARNING' && (
                                <Stack>
                                    <HStack>
                                        <Box>
                                            <HStack>
                                                {isCompliance ? (
                                                    getCompliancesIcon(myCompliances as number)
                                                ) : (
                                                    <Icon
                                                        path={mdiAccount}
                                                        size="11px"
                                                        color={
                                                            isSelected || colorMode !== 'light'
                                                                ? 'var(--chakra-colors-white)'
                                                                : 'var(--chakra-colors-dark-700)'
                                                        }
                                                    />
                                                )}

                                                <Text
                                                    as="span"
                                                    fontSize="12px"
                                                    letterSpacing=".5px"
                                                    textTransform="uppercase"
                                                    color={textColor}
                                                >
                                                    {howMany} {bodyText}
                                                </Text>
                                            </HStack>
                                            <HStack>
                                                <InfoIcon days={days} isSelected={isSelected} status={status} />
                                                <Text as="span" fontSize="21px" fontWeight="bold" letterSpacing="0.25px" color={textColor}>
                                                    {days !== null && getDaysText(days, status)}
                                                </Text>
                                            </HStack>
                                        </Box>
                                        {isCompliance && hasDirects && (
                                            <CompliancesContent
                                                id={id}
                                                isSelected={isSelected}
                                                textColor={textColor}
                                                myCompliances={myCompliances as number}
                                                directsCompliances={directsCompliances as number}
                                            />
                                        )}
                                    </HStack>
                                </Stack>
                            )}
                        </Stack>
                    )}

                    {!isExpanded && isMobile && (
                        <Box p="10px" background={color} {...cardClickProp()} {...(!isSelected && unselectBackgroundProps)}>
                            {!!requestNumber && <InfoIcon days={days} isSelected={isSelected} isMobile={true} status={status} />}
                            <SystemIcon
                                size="20px"
                                color={
                                    requestNumber
                                        ? isSelected
                                            ? 'var(--chakra-colors-white)'
                                            : 'default'
                                        : 'var(--chakra-colors-surface-medium)'
                                }
                                system={id}
                            />
                        </Box>
                    )}
                </Card.Body>
            </Card.Root>
        </>
    )
}

export default Widget
